import { supabaseClient } from './supabase';
import {
  VmtReductionComponents,
  getDefaultVmtComponents,
  ParkingPricingAssumptions,
  MicromobilityAssumptions,
} from '../types/policyImpact';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { getBuildingsEnergyData, calculateTotalElectricityConsumption, calculateTotalNaturalGasConsumption } from './buildingsEnergyDataService';
import { fetchCarbonIntensityData } from './carbonIntensityService';

// Policy scenario configuration
export interface PolicyScenario {
  evAdoptionPercent: number; // 0-100
  vmtReductionPercent: number; // 0-100 (computed from components)
  vmtReductionComponents: VmtReductionComponents; // NEW: component breakdown
  parkingPricing: ParkingPricingAssumptions; // NEW: parking pricing policy
  micromobility?: MicromobilityAssumptions; // NEW: expand micromobility program
  energyEfficiencyPercent: number; // 0-40 by 2045
  heatPumpAdoptionPercent: number; // 0-100
  gridCleanElectricityYear: number; // year electricity reaches zero emissions (2025-2045), default 2045 (SB 100)
  solarCapacityMultiplier: number; // 1.0 = current, 2.0 = double, etc.
  hdvElectrificationPercent: number; // 0-100
  organicsDiversionPercent: number; // 50-100% by 2045
}

// Baseline GHG data
export interface BaselineGHGData {
  lightDutyGHG: number; // metric tons CO2e
  heavyDutyGHG: number; // metric tons CO2e
  naturalGasGHG: number; // metric tons CO2e
  electricityGHG: number; // metric tons CO2e
  wasteGHG: number; // metric tons CO2e
  electricityKwh: number; // kWh/year (from Buildings page)
  electricityCarbonIntensityGPerKwh: number; // gCO2e/kWh (2023 from Buildings page)
  totalGHG: number; // metric tons CO2e
  vmt: number; // vehicle miles traveled
  naturalGasTherms: number;
  solarCapacityKw: number;
  currentEVPercent: number; // 2045 BAU EV% (used as slider default/minimum)
  currentEVPercent2023?: number; // 2023 EV% (for display)
  population: number;
}

// Policy impact results
export interface PolicyImpactResults {
  baseline: BaselineGHGData;
  projected: {
    lightDutyGHG: number;
    heavyDutyGHG: number;
    naturalGasGHG: number;
    electricityGHG: number; // projected electricity emissions (efficiency applied; electrification impact handled later)
    wasteGHG: number;
    totalGHG: number;
  };
  reductions: {
    lightDutyReduction: number;
    heavyDutyReduction: number;
    naturalGasReduction: number;
    electricityReduction: number;
    wasteReduction: number;
    totalReduction: number;
    percentReduction: number;
  };
  contributionsByPolicy: {
    evAdoption: number;
    vmtReduction: number;
    vmtReductionByComponent?: {
      transitOrientedDevelopment: number;
      activeTransportation: number;
      transitImprovements: number;
    };
    parkingPricing: number;
    micromobility?: number;
    energyEfficiency: number;
    heatPumpAdoption: number;
    solarCapacity: number;
    hdvElectrification: number;
    organicsDiversion: number;
  };
}

/**
 * Fetch EMFAC data for Policy Tool (same approach as GHG Inventory)
 * @param location - Location name
 * @param emfacVersion - '2021' or '2025' to select EMFAC version
 */
async function fetchEMFACDataForPolicy(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<any[]> {
  // Map city to county for EMFAC queries
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const emfacLocation = `${baseCountyName} County`;
  
  // Select table based on EMFAC version
  const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';
  
  // Try different location name variations (same as GHG Inventory)
  const locationVariations = [
    emfacLocation,
    baseCountyName,
    `${baseCountyName} County`
  ];
  
  for (const loc of locationVariations) {
    console.log(`Policy Impact: Trying EMFAC${emfacVersion} county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('year, ghg_per_mile')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    
    if (error) {
      console.log(`Policy Impact: EMFAC${emfacVersion} county filtered query error for`, loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      console.log(`Policy Impact: EMFAC${emfacVersion} county filtered data found for`, loc, ':', data);
      return data;
    }
  }
  
  console.log(`Policy Impact: No EMFAC${emfacVersion} county filtered data found for any location variation:`, locationVariations);
  
  // Fallback: Try California data (same as GHG Inventory)
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    console.log(`Policy Impact: Trying California EMFAC${emfacVersion} county filtered: ${californiaName}`);
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from(tableName)
      .select('year, ghg_per_mile')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.log(`Policy Impact: California EMFAC${emfacVersion} county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaData && californiaData.length > 0) {
      console.log(`Policy Impact: Using California EMFAC${emfacVersion} county filtered data for ${californiaName}:`, californiaData);
      return californiaData;
    }
  }
  
  console.log(`Policy Impact: No EMFAC${emfacVersion} data available`);
  return [];
}

// Constants for calculations
const CONSTANTS = {
  // Grid carbon intensity (kg CO2/kWh)
  BASELINE_GRID_INTENSITY: 0.2, // California 2023

  // EV constants
  EV_KWH_PER_MILE: 0.3, // Average EV efficiency
  ICE_IMPROVEMENT_FACTOR: 0.95, // Future ICE efficiency improvement

  // Heat pump constants
  HEAT_PUMP_COP: 3.0, // Coefficient of performance
  KWH_PER_THERM: 29.3, // Energy content conversion
  THERMS_PER_MT_CO2: 0.005291, // Natural gas emissions factor

  // Heavy duty vehicle constants
  HDV_ELECTRIFICATION_EFFICIENCY: 0.4, // EVs use 40% of energy of diesel

  // Solar constants
  SOLAR_DISPLACEMENT_FACTOR: 0.8, // Accounts for curtailment, storage, etc.
  ANNUAL_SOLAR_HOURS: 2000, // Average annual solar production hours
};

/**
 * Fetch baseline GHG data for a location using the same data source as GHG inventory
 */
export async function fetchBaselineGHGData(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<BaselineGHGData> {
  try {
    // Use the same data source as the GHG inventory chart
    const inventoryData = await fetchGHGInventoryData(location);
    
    // Extract data from the inventory result - this already includes all the complex calculations
    const lightDutyGHG = inventoryData.lightDutyEmissions || 0;
    const heavyDutyGHG = inventoryData.heavyDutyEmissions || 0;
    const naturalGasGHG = inventoryData.naturalGasEmissions || 0;
    const totalGHG = inventoryData.totalEmissions || 0;
    
    // Get the latest year from inventory data
    const latestYear = inventoryData.latestYear || 2023;
    
    // Get VMT data directly from the same source as GHG Inventory
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
    } catch (error) {
      console.warn('Could not get geofips for VMT data:', error);
    }
    
    // Import and use the same VMT data service as GHG Inventory
    const { getVMTDataForGHG } = await import('./vmtDataService');
    const vmtData = await getVMTDataForGHG(location, geofips || undefined, emfacVersion);
    
    // Get the latest VMT value (2023)
    const latestVmtData = vmtData.find(d => d.year === latestYear);
    const vmt = latestVmtData?.vmt || 0;
    
    console.log('Policy Tool: VMT data for', location, ':', vmtData.length, 'records');
    console.log('Policy Tool: Latest VMT (', latestYear, '):', vmt);
    
    // Calculate natural gas therms (approximate conversion)
    // Use Buildings-page consumption for therms + kWh at latestYear (defaults to 2023),
    // so policy math can do therms->kWh conversions consistently.
    let electricityKwh = 0;
    let naturalGasTherms = 0;
    try {
      const buildings = await getBuildingsEnergyData(location);
      electricityKwh = calculateTotalElectricityConsumption(buildings, latestYear);
      naturalGasTherms = calculateTotalNaturalGasConsumption(buildings, latestYear);
    } catch (e) {
      console.warn('Could not fetch Buildings energy consumption for baseline; defaulting to 0s:', e);
      electricityKwh = 0;
      naturalGasTherms = 0;
    }

    // Use Buildings-page carbon intensity series as the starting point (2023).
    let electricityCarbonIntensityGPerKwh = 300; // fallback gCO2e/kWh
    try {
      await supabaseLocationService.initialize();
      const resolvedGeofips = await supabaseLocationService.getGeofips(location);
      if (resolvedGeofips) {
        const ci = await fetchCarbonIntensityData(String(resolvedGeofips));
        const years = (ci.chartData?.labels || []) as any[];
        const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
        const idx = years.findIndex((y: any) => Number(y) === Number(latestYear) || String(y) === String(latestYear));
        if (idx >= 0) {
          const v = Number(data[idx]);
          if (Number.isFinite(v) && v > 0) electricityCarbonIntensityGPerKwh = v;
        }
      }
    } catch (e) {
      console.warn('Could not fetch carbon intensity for baseline; using fallback:', e);
    }

    // Baseline electricity emissions from kWh * (g/kWh)
    const electricityGHG = (electricityKwh * electricityCarbonIntensityGPerKwh) / 1_000_000;
    
    // Extract waste emissions from inventory chart data
    let wasteGHG = 0;
    try {
      const chartData = inventoryData.chartData;
      if (chartData?.datasets) {
        const wasteDataset = chartData.datasets.find((d: any) => d.label === 'Waste' || d.label?.includes('Waste'));
        if (wasteDataset?.data) {
          const labels = chartData.labels || [];
          const yearIndex = labels.findIndex((y: any) => Number(y) === Number(latestYear) || String(y) === String(latestYear));
          if (yearIndex >= 0 && yearIndex < wasteDataset.data.length) {
            const v = Number(wasteDataset.data[yearIndex]);
            if (Number.isFinite(v) && v > 0) wasteGHG = v;
          }
        }
      }
    } catch (e) {
      console.warn('Could not extract waste emissions from inventory chart data; defaulting to 0:', e);
    }
    
    // Fetch solar capacity using county or geofips to avoid city-name 400s
    let solarCapacityKw = 0;
    try {
      let solarQuery = supabaseClient
        .from('all_pv_size')
        .select('kw_total')
        .eq('year', latestYear);

      if (geofips) {
        solarQuery = solarQuery.eq('geoid', geofips);
      } else {
        const countyName = supabaseLocationService.getCountyForCity(location) || location;
        // Prefer county aggregation; fall back to city rows within county
        const { data: countyRows } = await solarQuery
          .ilike('location_name', `%${(countyName || '').replace(' County', '').replace(' Co.', '')}%`)
          .eq('geography', 'County');
        if (countyRows && countyRows.length > 0) {
          solarCapacityKw = countyRows.reduce((sum, row: any) => sum + (row.kw_total || 0), 0);
        } else {
          const { data: cityRows } = await supabaseClient
            .from('all_pv_size')
            .select('kw_total, county, geography')
            .eq('year', latestYear)
            .ilike('county', `%${(countyName || '').replace(' County', '').replace(' Co.', '')}%`)
            .eq('geography', 'City');
          solarCapacityKw = cityRows?.reduce((sum, row: any) => sum + (row.kw_total || 0), 0) || 0;
        }
      }
    } catch (e) {
      console.warn('Solar capacity lookup failed, defaulting to 0:', e);
      solarCapacityKw = 0;
    }
    
    // Fetch EV adoption data from emfac2021_county (2021) or emfac2025_county (2025) for both 2023 (current) and 2045 (BAU) at county level
    const evTableName = emfacVersion === '2025' ? 'emfac2025_county' : 'emfac2021_county';
    let totalEVs2023 = 0;
    let totalVehicles2023 = 0;
    let totalEVs2045 = 0;
    let totalVehicles2045 = 0;
    
    try {
      await supabaseLocationService.initialize();
      
      // Get county name for the location (EMFAC data is at county level)
      // If location is already a county, use it directly; otherwise get county from city
      const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
      const baseCountyName = resolvedCounty.replace(/\s+County$/i, '').trim();
      const countyName = `${baseCountyName} County`;
      
      // Try different county name variations
      const locationVariations = [
        countyName,
        baseCountyName,
        resolvedCounty,
        location // Also try the original location name in case it's already a county
      ];
      
      console.log(`🔍 Looking up EV adoption for location: "${location}" using ${evTableName} (EMFAC${emfacVersion})`);
      console.log(`   Resolved county: "${resolvedCounty}"`);
      console.log(`   Trying variations:`, locationVariations);
      
      for (const loc of locationVariations) {
        // Get 2023 EV data (current year)
        // Note: Only count "Electricity" as EV fuel type (exclude PHEV and FCEV)
        const { data: evData2023, error: evError2023 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('fuel', 'Electricity')
          .eq('year', 2023);

        const { data: allLdvData2023, error: allLdvError2023 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('year', 2023);

        // Get 2045 EV data (BAU projection - this is the default for slider)
        // Note: Only count "Electricity" as EV fuel type (exclude PHEV and FCEV)
        const { data: evData2045, error: evError2045 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('fuel', 'Electricity')
          .eq('year', 2045);

        const { data: allLdvData2045, error: allLdvError2045 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('year', 2045);

        if (!evError2023 && evData2023 && evData2023.length > 0) {
          totalEVs2023 = evData2023.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }

        if (!allLdvError2023 && allLdvData2023 && allLdvData2023.length > 0) {
          totalVehicles2023 = allLdvData2023.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }

        if (!evError2045 && evData2045 && evData2045.length > 0) {
          totalEVs2045 = evData2045.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }

        if (!allLdvError2045 && allLdvData2045 && allLdvData2045.length > 0) {
          totalVehicles2045 = allLdvData2045.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }
        
        // If we found 2045 data, break out of the loop (2045 is what we need for the slider default)
        if (totalVehicles2045 > 0) {
          console.log(`✅ Found EV adoption data for "${loc}":`);
          console.log(`   2023: ${totalEVs2023.toLocaleString()} EVs / ${totalVehicles2023.toLocaleString()} total LDV vehicles`);
          console.log(`   2045: ${totalEVs2045.toLocaleString()} EVs / ${totalVehicles2045.toLocaleString()} total LDV vehicles`);
          break;
        } else if (evError2045 || allLdvError2045) {
          console.warn(`⚠️ Error querying "${loc}":`, evError2045 || allLdvError2045);
        }
      }
      
      if (totalVehicles2045 === 0) {
        console.warn(`⚠️ No EV adoption data found for any location variation. Tried:`, locationVariations);
        console.warn(`   This may indicate missing data in ${evTableName} for year 2045`);
      }
    } catch (e) {
      console.error(`❌ EV adoption lookup from ${evTableName} failed:`, e);
      console.warn('   Defaulting to 0% for EV adoption slider');
    }
    
    // Calculate EV percentages
    // 2023: Current EV percentage (for display)
    const currentEVPercent2023 = totalVehicles2023 > 0 ? Number(((totalEVs2023 / totalVehicles2023) * 100).toFixed(2)) : 0;
    
    // 2045: BAU EV percentage (this is the default/minimum for the slider)
    const currentEVPercent = totalVehicles2045 > 0 ? Number(((totalEVs2045 / totalVehicles2045) * 100).toFixed(2)) : 0;
    
    console.log(`📊 EV Adoption: 2023=${currentEVPercent2023.toFixed(2)}%, 2045 BAU=${currentEVPercent.toFixed(2)}% (slider default)`);
    
    // Fetch population
    const { data: popData } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', location)
      .eq('year', latestYear)
      .single();
    
    const population = popData?.population || 0;

    console.log(`Fetched baseline GHG data for ${location} (${latestYear}):`, {
      lightDutyGHG,
      heavyDutyGHG,
      naturalGasGHG,
      electricityGHG,
      wasteGHG,
      totalGHG,
      vmt,
      naturalGasTherms,
      solarCapacityKw,
      currentEVPercent,
      population
    });

    return {
      lightDutyGHG,
      heavyDutyGHG,
      naturalGasGHG,
      electricityGHG,
      wasteGHG,
      electricityKwh,
      electricityCarbonIntensityGPerKwh,
      totalGHG,
      vmt,
      naturalGasTherms,
      solarCapacityKw,
      currentEVPercent, // 2045 BAU EV% (used as slider default/minimum)
      currentEVPercent2023, // 2023 EV% (for display)
      population,
    };
  } catch (error) {
    console.error('Error fetching baseline GHG data:', error);
    throw error;
  }
}

/**
 * Calculate grid carbon intensity based on solar penetration
 */
function calculateGridCarbonIntensity(
  solarCapacityKw: number,
  population: number
): number {
  // Estimate total electricity consumption (kWh/year)
  // California average: ~7,000 kWh per capita per year
  const totalElectricityConsumption = population * 7000;

  // Estimate solar generation (kWh/year)
  const solarGeneration = solarCapacityKw * CONSTANTS.ANNUAL_SOLAR_HOURS;

  // Calculate solar penetration
  const solarPenetration = totalElectricityConsumption > 0
    ? solarGeneration / totalElectricityConsumption
    : 0;

  // Calculate new grid carbon intensity
  const gridIntensity = CONSTANTS.BASELINE_GRID_INTENSITY *
    (1 - Math.min(solarPenetration * CONSTANTS.SOLAR_DISPLACEMENT_FACTOR, 0.9));

  return gridIntensity;
}

/**
 * Calculate VMT reduction from parking pricing policy
 * Uses elasticity-based approach from Pierce & Shoup 2013
 */
function calculateParkingPricingVMTReduction(
  parkingAssumptions: ParkingPricingAssumptions,
  baselineVMT: number
): number {
  if (parkingAssumptions.priceIncreasePercent === 0) return 0;
  
  const SF_COMMUTE_BY_CAR_PERCENT = 30.29; // San Francisco baseline
  const BASE_ELASTICITY = -0.4; // Pierce and Shoup 2013
  
  // Calculate elasticity based on commute by car percentage
  let elasticity = 0;
  if (parkingAssumptions.commuteByCarPercent > 0 && parkingAssumptions.commuteByCarPercent < 100) {
    elasticity = BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / parkingAssumptions.commuteByCarPercent);
  }
  
  // Calculate percent VMT reduction based on elasticity and price change
  const vmtReductionPercent = Math.abs(elasticity) * (parkingAssumptions.priceIncreasePercent / 100);
  
  // Apply only to the percentage of VMT that can be affected
  const effectiveReductionPercent = vmtReductionPercent * (parkingAssumptions.vmtAffectedPercent / 100);
  
  return effectiveReductionPercent * 100; // Return as percentage
}

// Calculate micromobility GHG reduction (tCO2e/year)
async function calculateMicromobilityReduction(
  location: string,
  assumptions: MicromobilityAssumptions,
  emfacGhgPerMile: number,
  gridCarbonIntensity: number
): Promise<number> {
  try {
    // Fetch latest population (use 2025+ nearest) for location
    const { data: popRows } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', location)
      .order('year', { ascending: false })
      .limit(1);
    const population = popRows?.[0]?.population || 0;

    const scale = Math.max(0, assumptions.scalePercent || 0) / 100; // 0-2.0

    // Trips per resident
    const totalTrips = (assumptions.tripsPerResident || 0) * population * scale;
    const bikeTrips = totalTrips * ((assumptions.percentBike || 0) / 100);
    const scooterTrips = totalTrips * ((assumptions.percentScooter || 0) / 100);

    // Annual miles
    const bikeMiles = bikeTrips * (assumptions.milesPerBikeTrip || 0);
    const scooterMiles = scooterTrips * (assumptions.milesPerScooterTrip || 0);

    // Displaced car miles
    const displacedCarMiles =
      bikeMiles * ((assumptions.percentBikeTripsDisplaceCars || 0) / 100) +
      scooterMiles * ((assumptions.percentScooterTripsDisplaceCars || 0) / 100);

    // Avoided car emissions
    const avoidedCarTons = displacedCarMiles * emfacGhgPerMile; // tons CO2e

    // Electricity use and emissions
    const kwhBike = bikeMiles * (assumptions.kwhPerMileBike || 0);
    const kwhScooter = scooterMiles * (assumptions.kwhPerMileScooter || 0);
    // gridCarbonIntensity is kg CO2 per kWh in this file's constants; convert to tons
    const electricityTons = ((kwhBike + kwhScooter) * gridCarbonIntensity) / 1000;

    const netTons = Math.max(0, avoidedCarTons - electricityTons);
    return netTons;
  } catch (e) {
    console.warn('Micromobility reduction calculation failed:', e);
    return 0;
  }
}

// Detailed stats for Micromobility policy (for UI display)
export interface MicromobilityStats {
  population: number;
  scalePercent: number;
  tripsPerResident: number;
  totalProgramTrips: number;
  bikeTrips: number;
  scooterTrips: number;
  bikeMiles: number;
  scooterMiles: number;
  displacedCarMiles: number; // VMT reduced
  emfacGhgPerMile: number; // tons/mile used
  avoidedCarTons: number;
  electricityKwh: number;
  electricityTons: number;
  netTons: number;
}

export async function getMicromobilityStats(
  location: string,
  scenario: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025'
): Promise<MicromobilityStats> {
  const baseline = await fetchBaselineGHGData(location, emfacVersion);
  // Determine emission factor consistent with baseline
  const emfacData = await fetchEMFACDataForPolicy(location, emfacVersion);
  const validEmissionFactors = emfacData.filter((d) => d.ghg_per_mile > 0);
  const ef2023 = validEmissionFactors.find((d: any) => d.year === 2023)?.ghg_per_mile;
  const avgEmissionFactor = validEmissionFactors.length > 0
    ? validEmissionFactors.reduce((sum: number, d: any) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
    : 0.0008;
  const emfacGhgPerMile = ef2023 || (baseline.lightDutyGHG / Math.max(1, baseline.vmt)) || avgEmissionFactor;

  // Grid carbon intensity based on current solar scale (kg/kWh)
  const gridCarbonIntensity = calculateGridCarbonIntensity(
    baseline.solarCapacityKw * (scenario.solarCapacityMultiplier || 1),
    baseline.population
  );

  const a = scenario.micromobility || {
    yearImplemented: 2025,
    tripsPerResident: 3,
    percentBike: 60,
    percentScooter: 40,
    milesPerBikeTrip: 1.61,
    milesPerScooterTrip: 1.2,
    percentBikeTripsDisplaceCars: 30,
    percentScooterTripsDisplaceCars: 20,
    kwhPerMileBike: 0.02,
    kwhPerMileScooter: 0.04,
    scalePercent: 0,
  };

  const scale = Math.max(0, a.scalePercent || 0) / 100; // 0-2.0
  const tripsPerResident = a.tripsPerResident || 0;
  const totalProgramTrips = baseline.population * tripsPerResident * scale;
  const bikeTrips = totalProgramTrips * ((a.percentBike || 0) / 100);
  const scooterTrips = totalProgramTrips * ((a.percentScooter || 0) / 100);

  const bikeMiles = bikeTrips * (a.milesPerBikeTrip || 0);
  const scooterMiles = scooterTrips * (a.milesPerScooterTrip || 0);

  const displacedCarMiles =
    bikeMiles * ((a.percentBikeTripsDisplaceCars || 0) / 100) +
    scooterMiles * ((a.percentScooterTripsDisplaceCars || 0) / 100);

  const avoidedCarTons = displacedCarMiles * emfacGhgPerMile;
  const electricityKwh = bikeMiles * (a.kwhPerMileBike || 0) + scooterMiles * (a.kwhPerMileScooter || 0);
  const electricityTons = (electricityKwh * gridCarbonIntensity) / 1000;
  const netTons = Math.max(0, avoidedCarTons - electricityTons);

  return {
    population: baseline.population,
    scalePercent: a.scalePercent || 0,
    tripsPerResident,
    totalProgramTrips: Math.round(totalProgramTrips),
    bikeTrips: Math.round(bikeTrips),
    scooterTrips: Math.round(scooterTrips),
    bikeMiles: Math.round(bikeMiles),
    scooterMiles: Math.round(scooterMiles),
    displacedCarMiles: Math.round(displacedCarMiles),
    emfacGhgPerMile,
    avoidedCarTons: Math.round(avoidedCarTons),
    electricityKwh: Math.round(electricityKwh),
    electricityTons: Math.round(electricityTons * 10) / 10,
    netTons: Math.round(netTons * 10) / 10,
  };
}

/**
 * Calculate policy impact on GHG emissions
 * @param location - Location name
 * @param scenario - Policy scenario
 * @param emfacVersion - '2021' or '2025' to select EMFAC version
 */
export async function calculatePolicyImpact(
  location: string,
  scenario: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025'
): Promise<PolicyImpactResults> {
  const baseline = await fetchBaselineGHGData(location, emfacVersion);

  // Grid decarbonization lever is modeled as the year electricity reaches zero emissions (g/kWh -> 0).
  // In this 2045 summary, electricity is either clean (if year <= 2045) or not.
  // Since the slider range is 2025–2045, by definition electricity is clean by 2045.
  // (The lever mainly changes cumulative emissions before 2045, which is visible in the time series.)
  const gPerKwh2045 = 0;

  // Keep existing solar logic for EV/grid interactions (separate from the grid decarb lever for now).
  const newSolarCapacity = baseline.solarCapacityKw * scenario.solarCapacityMultiplier;
  const gridCarbonIntensity = calculateGridCarbonIntensity(newSolarCapacity, baseline.population);
  const baselineGridIntensity = calculateGridCarbonIntensity(baseline.solarCapacityKw, baseline.population);
  const gridDecarbonizationFactor = baselineGridIntensity > 0 ? (gridCarbonIntensity / baselineGridIntensity) : 1;

  // === Light Duty Vehicle Calculations ===
  // Calculate parking pricing VMT reduction first
  const parkingVMTReduction = calculateParkingPricingVMTReduction(
    scenario.parkingPricing,
    baseline.vmt
  );
  
  // Total VMT reduction includes both policy-based and parking pricing reductions
  const totalVMTReductionPercent = scenario.vmtReductionPercent + parkingVMTReduction;
  const newVMT = baseline.vmt * (1 - totalVMTReductionPercent / 100);
  const newEVPercent = Math.min(scenario.evAdoptionPercent, 100);
  const icePercent = 100 - newEVPercent;
  
  // Calculate new fleet-weighted GHG per mile using EMFAC data (same as GHG Inventory)
  const baselineGhgPerMile = baseline.lightDutyGHG / baseline.vmt;
  
  // Get EMFAC data for accurate emissions per mile (same as GHG Inventory)
  const emfacData = await fetchEMFACDataForPolicy(location, emfacVersion);
  
  // Prefer the 2023 EMFAC factor to match baseline; fall back to average
  const validEmissionFactors = emfacData.filter(d => d.ghg_per_mile > 0);
  const ef2023 = validEmissionFactors.find(d => d.year === 2023)?.ghg_per_mile;
  const avgEmissionFactor = validEmissionFactors.length > 0
    ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
    : 0.0008; // Fallback (tons CO2 per mile)

  // Align calculation baseline with inventory by using baselineGhgPerMile when available
  const emfacGhgPerMile = ef2023 || baselineGhgPerMile || avgEmissionFactor;
  console.log('Policy Impact: Using EMFAC factor:', emfacGhgPerMile, '(2023 preferred, else baseline/avg)');
  
  // Use EMFAC-based emissions per mile for ICE vehicles
  const iceGhgPerMile = emfacGhgPerMile * CONSTANTS.ICE_IMPROVEMENT_FACTOR;
  // gridCarbonIntensity is in kg CO2/kWh, EV_KWH_PER_MILE is kWh/mile
  // Result is kg CO2/mile, convert to tons CO2/mile by dividing by 1000
  const evGhgPerMile = (gridCarbonIntensity * CONSTANTS.EV_KWH_PER_MILE) / 1000;

  const newFleetGhgPerMile = (icePercent / 100 * iceGhgPerMile) + (newEVPercent / 100 * evGhgPerMile);
  const newLightDutyGHG = newVMT * newFleetGhgPerMile;

  // === Heavy Duty Vehicle Calculations ===
  const hdvElectricPercent = scenario.hdvElectrificationPercent;
  const hdvICEPercent = 100 - hdvElectricPercent;

  const hdvElectricGHG = (hdvElectricPercent / 100) * baseline.heavyDutyGHG *
    CONSTANTS.HDV_ELECTRIFICATION_EFFICIENCY * gridDecarbonizationFactor;
  const hdvICEGHG = (hdvICEPercent / 100) * baseline.heavyDutyGHG;
  const newHeavyDutyGHG = hdvElectricGHG + hdvICEGHG;

  // === Buildings: kWh and carbon intensity are modeled separately ===
  // Requirements:
  // - Use Buildings-page kWh and 2023 carbon intensity (g/kWh) as starting point.
  // - Energy efficiency reduces kWh and therms (apply first).
  // - Building electrification replaces a share of remaining therms with electricity:
  //     1 therm = 29.3 kWh thermal; heat pump COP = 3.5 => added_kWh = therms_replaced * (29.3/3.5)
  // - Carbon intensity (grid decarbonization) applies to the remaining electricity emissions (after efficiency).
  const effPct = Math.max(0, Math.min(40, scenario.energyEfficiencyPercent || 0));
  const bldgElecPct = Math.max(0, Math.min(100, scenario.heatPumpAdoptionPercent || 0));
  const effFrac = effPct / 100;
  const elecFrac = bldgElecPct / 100;

  const COP = 3.5;
  const KWH_PER_THERM = 29.3;

  // Apply efficiency first
  const thermsAfterEff = baseline.naturalGasTherms * (1 - effFrac);
  const kwhAfterEff = baseline.electricityKwh * (1 - effFrac);

  // Then electrify a share of remaining therms
  const thermsReplaced = thermsAfterEff * elecFrac;
  const thermsRemaining = Math.max(0, thermsAfterEff - thermsReplaced);
  const addedKwh = thermsReplaced * (KWH_PER_THERM / COP);

  const totalKwh = kwhAfterEff + addedKwh;

  // Natural gas emissions from remaining therms (5.29 kg CO2e/therm)
  const newNaturalGasGHG = (thermsRemaining * 5.29) / 1000;

  // Electricity emissions from kWh * g/kWh; apply carbon-intensity factor after efficiency.
  const gPerKwh = gPerKwh2045;
  const newElectricityGHG = (totalKwh * gPerKwh) / 1_000_000;

  // Apply organics diversion reduction to waste emissions (50-100% by 2045)
  const organicsDiversionPercent = Math.max(50, Math.min(100, (scenario as any).organicsDiversionPercent || 50));
  const organicsDiversionFrac = organicsDiversionPercent / 100;
  const newWasteGHG = baseline.wasteGHG * (1 - organicsDiversionFrac);

  // === Total Projected GHG ===
  const totalProjectedGHG = newLightDutyGHG + newHeavyDutyGHG + newNaturalGasGHG + newElectricityGHG + newWasteGHG;

  // === Calculate reductions ===
  const lightDutyReduction = baseline.lightDutyGHG - newLightDutyGHG;
  const heavyDutyReduction = baseline.heavyDutyGHG - newHeavyDutyGHG;
  const naturalGasReduction = baseline.naturalGasGHG - newNaturalGasGHG;
  const electricityReduction = baseline.electricityGHG - newElectricityGHG;
  const wasteReduction = baseline.wasteGHG - newWasteGHG;
  const totalReduction = baseline.totalGHG - totalProjectedGHG;
  const percentReduction = (totalReduction / baseline.totalGHG) * 100;

  // === Calculate individual policy contributions (approximate) ===
  // These are calculated by running each policy in isolation

  // EV adoption impact (isolated)
  const evOnlyVMT = baseline.vmt;
  const evOnlyFleetGhgPerMile = ((100 - scenario.evAdoptionPercent) / 100 * emfacGhgPerMile) +
    (scenario.evAdoptionPercent / 100 * evGhgPerMile);
  const evOnlyLDGHG = evOnlyVMT * evOnlyFleetGhgPerMile;
  // Ensure zero adoption yields zero contribution; clamp small negatives due to factor mismatches
  const rawEvContribution = baseline.lightDutyGHG - evOnlyLDGHG;
  const evContribution = scenario.evAdoptionPercent === 0 ? 0 : Math.max(0, rawEvContribution);

  // VMT reduction impact (includes both traditional VMT reduction and parking pricing)
  const totalVMTReduction = scenario.vmtReductionPercent + parkingVMTReduction;
  const vmtOnlyReduction = baseline.lightDutyGHG * (totalVMTReduction / 100);

  // VMT reduction by component (proportional to each component's percentage)
  const vmtReductionByComponent = {
    transitOrientedDevelopment: baseline.lightDutyGHG * (scenario.vmtReductionComponents.transitOrientedDevelopment / 100),
    activeTransportation: baseline.lightDutyGHG * (scenario.vmtReductionComponents.activeTransportation / 100),
    transitImprovements: baseline.lightDutyGHG * (scenario.vmtReductionComponents.transitImprovements / 100),
  };
  
  // Parking pricing VMT reduction contribution
  const parkingPricingVMTReduction = calculateParkingPricingVMTReduction(
    scenario.parkingPricing,
    baseline.vmt
  );
  const parkingPricingContribution = baseline.lightDutyGHG * (parkingPricingVMTReduction / 100);

  // Energy efficiency impact (isolated, end-state) using the same kWh/therms accounting.
  // Efficiency alone reduces kWh and therms, without electrification adders.
  const effTherms = baseline.naturalGasTherms * (1 - effFrac);
  const effKwh = baseline.electricityKwh * (1 - effFrac);
  const effNgTons = (effTherms * 5.29) / 1000;
  const effElecTons = (effKwh * gPerKwh2045) / 1_000_000;
  const energyEfficiencyContribution =
    Math.max(0, (baseline.naturalGasGHG - effNgTons) + (baseline.electricityGHG - effElecTons));

  // Building electrification impact (isolated, end-state) using therms->kWh conversion.
  // Apply no efficiency, only electrification.
  const elecThermsReplaced = baseline.naturalGasTherms * elecFrac;
  const elecThermsRemaining = Math.max(0, baseline.naturalGasTherms - elecThermsReplaced);
  const elecAddedKwh = elecThermsReplaced * (KWH_PER_THERM / COP);
  const elecTotalKwh = baseline.electricityKwh + elecAddedKwh;
  const elecNgTons = (elecThermsRemaining * 5.29) / 1000;
  const elecElecTons = (elecTotalKwh * gPerKwh2045) / 1_000_000;
  const heatPumpContribution = Math.max(0, baseline.naturalGasGHG + baseline.electricityGHG - (elecNgTons + elecElecTons));

  // Solar capacity impact (on EVs and heat pumps)
  const solarGridImpact = (baselineGridIntensity - gridCarbonIntensity) / baselineGridIntensity;
  const solarContribution = (newEVPercent / 100 * newVMT * CONSTANTS.EV_KWH_PER_MILE * solarGridImpact) / 1000 +
    (newElectricityGHG * solarGridImpact);

  // HDV electrification impact
  const hdvContribution = baseline.heavyDutyGHG - newHeavyDutyGHG;

  // Organics diversion impact
  const organicsDiversionContribution = baseline.wasteGHG - newWasteGHG;

  // Micromobility impact (if configured)
  let micromobilityContribution = 0;
  if (scenario.micromobility && (scenario.micromobility.scalePercent || 0) > 0) {
    micromobilityContribution = await calculateMicromobilityReduction(
      location,
      scenario.micromobility,
      emfacGhgPerMile,
      gridCarbonIntensity
    );
  }

  return {
    baseline,
    projected: {
      lightDutyGHG: newLightDutyGHG,
      heavyDutyGHG: newHeavyDutyGHG,
      naturalGasGHG: newNaturalGasGHG,
      electricityGHG: newElectricityGHG,
      wasteGHG: newWasteGHG,
      totalGHG: totalProjectedGHG,
    },
    reductions: {
      lightDutyReduction,
      heavyDutyReduction,
      naturalGasReduction,
      electricityReduction,
      wasteReduction,
      totalReduction,
      percentReduction,
    },
    contributionsByPolicy: {
      evAdoption: evContribution,
      vmtReduction: vmtOnlyReduction,
      vmtReductionByComponent,
      parkingPricing: parkingPricingContribution,
      micromobility: micromobilityContribution,
      energyEfficiency: energyEfficiencyContribution,
      heatPumpAdoption: heatPumpContribution,
      solarCapacity: solarContribution,
      hdvElectrification: hdvContribution,
      organicsDiversion: organicsDiversionContribution,
    },
  };
}

/**
 * Get default policy scenario (baseline)
 */
export function getDefaultScenario(): PolicyScenario {
  return {
    evAdoptionPercent: 0,
    vmtReductionPercent: 0,
    vmtReductionComponents: getDefaultVmtComponents(),
    parkingPricing: {
      priceIncreasePercent: 50,
      vmtAffectedPercent: 15,
      startingPricePerHour: 0,
      commuteByCarPercent: 60, // Will be fetched from database
    },
    micromobility: {
      yearImplemented: 2025,
      tripsPerResident: 3,
      percentBike: 60,
      percentScooter: 40,
      milesPerBikeTrip: 1.61,
      milesPerScooterTrip: 1.2,
      percentBikeTripsDisplaceCars: 30,
      percentScooterTripsDisplaceCars: 20,
      kwhPerMileBike: 0.02,
      kwhPerMileScooter: 0.04,
      scalePercent: 0,
    },
    energyEfficiencyPercent: 0,
    heatPumpAdoptionPercent: 0,
    gridCleanElectricityYear: 2045,
    solarCapacityMultiplier: 1.0,
    hdvElectrificationPercent: 0,
    organicsDiversionPercent: 50,
  };
}

/**
 * Get predefined scenario examples
 */
export function getPredefinedScenarios(): { [key: string]: PolicyScenario } {
  return {
    baseline: {
      evAdoptionPercent: 0,
      vmtReductionPercent: 0,
      vmtReductionComponents: {
        transitOrientedDevelopment: 0,
        activeTransportation: 0,
        transitImprovements: 0,
      },
      parkingPricing: {
        priceIncreasePercent: 0,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      energyEfficiencyPercent: 0,
      heatPumpAdoptionPercent: 0,
      gridCleanElectricityYear: 2045,
      solarCapacityMultiplier: 1.0,
      hdvElectrificationPercent: 0,
      organicsDiversionPercent: 50, // Minimum is 50%
    },
    fullImplementation: {
      evAdoptionPercent: 100,
      vmtReductionPercent: 20, // Maximum is 20%
      vmtReductionComponents: {
        transitOrientedDevelopment: 8,
        activeTransportation: 4,
        transitImprovements: 8,
      },
      parkingPricing: {
        priceIncreasePercent: 300, // Maximum
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      energyEfficiencyPercent: 40, // Maximum is 40%
      heatPumpAdoptionPercent: 100,
      gridCleanElectricityYear: 2025, // Earliest (all the way right)
      solarCapacityMultiplier: 5.0,
      hdvElectrificationPercent: 100,
      organicsDiversionPercent: 100,
    },
    aggressive: {
      evAdoptionPercent: 80,
      vmtReductionPercent: 10,
      vmtReductionComponents: {
        transitOrientedDevelopment: 4,
        activeTransportation: 2,
        transitImprovements: 4,
      },
      parkingPricing: {
        priceIncreasePercent: 100,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      energyEfficiencyPercent: 20,
      heatPumpAdoptionPercent: 70,
      gridCleanElectricityYear: 2025,
      solarCapacityMultiplier: 3.0,
      hdvElectrificationPercent: 50,
      organicsDiversionPercent: 100,
    },
    moderate: {
      evAdoptionPercent: 50,
      vmtReductionPercent: 5,
      vmtReductionComponents: {
        transitOrientedDevelopment: 2,
        activeTransportation: 1,
        transitImprovements: 2,
      },
      parkingPricing: {
        priceIncreasePercent: 50,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      energyEfficiencyPercent: 10,
      heatPumpAdoptionPercent: 40,
      gridCleanElectricityYear: 2030,
      solarCapacityMultiplier: 2.0,
      hdvElectrificationPercent: 25,
      organicsDiversionPercent: 50, // Default (not specified)
    },
  };
}
